import React, { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { Loader2, Send } from 'lucide-react';

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export const MessagesChat = ({ otherId, otherName }) => {
  const [messages, setMessages] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async (initial = false) => {
    try {
      const d = await api.messagesWith(otherId);
      setMessages(d.messages || []);
      setMe(d.me);
    } catch (_) { /* silent */ }
    if (initial) setLoading(false);
  }, [otherId]);

  useEffect(() => {
    setLoading(true);
    load(true);
    const iv = setInterval(() => load(), 8000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const r = await api.messagesSend(otherId, { body: text });
      setMessages((prev) => [...prev, r.message]);
      setBody('');
    } catch (_) {
      setBody(text);
      window.alert("Échec de l'envoi — réessaie dans un instant.");
    }
    setSending(false);
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid={`chat-thread-${otherId}`}>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 min-h-[300px] max-h-[55vh]">
        {loading ? (
          <div className="flex items-center gap-2 text-white/50 justify-center py-10"><Loader2 size={15} className="animate-spin" /> Chargement…</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-white/40 text-[13px] py-10">Aucun message pour l&apos;instant — écris le premier à {otherName} 👋</div>
        ) : messages.map((m) => {
          const mine = m.sender_id === me;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 ${mine ? 'bg-red-600 text-white rounded-br-sm' : 'bg-white/8 text-white/90 rounded-bl-sm border border-white/10'}`}>
                <div className="text-[13.5px] leading-relaxed whitespace-pre-wrap break-words">{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-white/40'}`}>{fmtTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-white/10 p-3 flex items-end gap-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder={`Message à ${otherName}…`}
          data-testid="chat-input"
          className="flex-1 rounded-xl bg-black/40 border border-white/15 px-3 py-2.5 text-white text-[13.5px] leading-relaxed focus:border-red-500 outline-none resize-none"
        />
        <button
          onClick={send}
          disabled={sending || !body.trim()}
          data-testid="chat-send-btn"
          className="h-10 w-10 shrink-0 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white inline-flex items-center justify-center"
          aria-label="Envoyer"
        >
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        </button>
      </div>
    </div>
  );
};
